import { addDays, format, parseISO } from "date-fns";

import {
  EmailSequence,
  EmailSequencePayload,
  EmailSequenceStep,
  EmailSequenceTemplate,
  EmailSequenceTemplatePayload,
  SequenceForm,
  SequenceStepForm,
  SequenceStopScope,
} from "./emailSequence.types";

export const MAX_SEQUENCE_STEPS = 10;
export const DEFAULT_FOLLOW_UP_DAYS = 3;

let stepKey = 0;
const nextKey = () => `step-${++stepKey}`;

export const localTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
};

export const todayIso = (today = new Date()) => format(today, "yyyy-MM-dd");

export const newStep = (
  index: number,
  template?: { id: string; subject: string; body: string },
): SequenceStepForm => ({
  key: nextKey(),
  emailTemplateId: template?.id || "",
  subject: template?.subject || "",
  body: template?.body || "",
  sendMode: index === 0 ? "immediately" : "afterDays",
  delayDays: index === 0 ? 0 : DEFAULT_FOLLOW_UP_DAYS,
  scheduledDate: "",
});

export const getDefaultForm = ({
  name,
  fromUserId,
  toEmail,
}: {
  name: string;
  fromUserId: string;
  toEmail: string;
}): SequenceForm => ({
  name,
  fromUserId,
  toEmail,
  stopScope: "all",
  replyStatusId: "",
  notifyUserIds: [],
  steps: [newStep(0), newStep(1)],
});

/**
 * Fills the form from a Settings template. In templates the first email is
 * "immediately, or after N days"; here that becomes a scheduled date.
 */
export const applySequenceTemplate = (
  form: SequenceForm,
  template: EmailSequenceTemplate,
  statuses: { id: string; name: string }[],
  today = new Date(),
): SequenceForm => ({
  ...form,
  stopScope: template.stopScope,
  replyStatusId:
    statuses.find(
      (status) =>
        status.name.toLowerCase() ===
        (template.replyStatusName || "").toLowerCase(),
    )?.id || form.replyStatusId,
  notifyUserIds: template.notifyUserIds?.length
    ? template.notifyUserIds
    : form.notifyUserIds,
  steps: template.steps
    .slice()
    .sort((a, b) => a.order - b.order)
    .slice(0, MAX_SEQUENCE_STEPS)
    .map((step, index) => {
      const days = Math.max(step.delayDays || 0, 0);
      const delayedFirst = index === 0 && days > 0;
      return {
        key: nextKey(),
        emailTemplateId: step.emailTemplateId || "",
        subject: step.subject,
        body: step.body,
        sendMode:
          index === 0 ? (delayedFirst ? "date" : "immediately") : "afterDays",
        delayDays: index === 0 ? 0 : days,
        scheduledDate: delayedFirst ? todayIso(addDays(today, days)) : "",
      };
    }),
});

export const formFromSequence = (sequence: EmailSequence): SequenceForm => ({
  name: sequence.name,
  fromUserId: sequence.fromUserId,
  toEmail: sequence.toEmail,
  stopScope: sequence.stopScope,
  replyStatusId: sequence.replyStatusId || "",
  notifyUserIds: sequence.notifyUserIds || [],
  steps: sequence.steps
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((step) => ({
      key: nextKey(),
      emailTemplateId: step.emailTemplateId || "",
      subject: step.subject,
      body: step.body,
      sendMode: step.sendMode,
      delayDays: step.delayDays ?? 0,
      scheduledDate: step.scheduledDate || "",
      sentAt: step.sentAt,
    })),
});

export const formFromTemplate = (
  template: EmailSequenceTemplate,
): SequenceForm => ({
  name: template.name,
  fromUserId: "",
  toEmail: "",
  stopScope: template.stopScope,
  replyStatusId: "",
  notifyUserIds: template.notifyUserIds || [],
  steps: template.steps
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((step, index) => ({
      key: nextKey(),
      emailTemplateId: step.emailTemplateId || "",
      subject: step.subject,
      body: step.body,
      sendMode: index === 0 ? "immediately" : "afterDays",
      delayDays: step.delayDays ?? (index === 0 ? 0 : DEFAULT_FOLLOW_UP_DAYS),
      scheduledDate: "",
    })),
});

const toStep = (step: SequenceStepForm, index: number): EmailSequenceStep => ({
  order: index + 1,
  emailTemplateId: step.emailTemplateId || null,
  subject: step.subject.trim(),
  body: step.body,
  sendMode: index === 0 ? step.sendMode : "afterDays",
  delayDays:
    index === 0 && step.sendMode !== "beforeYearEnd"
      ? null
      : Math.max(Number(step.delayDays) || 0, 0),
  scheduledDate:
    index === 0 && step.sendMode === "date" ? step.scheduledDate : null,
});

export const buildSequencePayload = (
  form: SequenceForm,
  context: {
    id?: string;
    orgId: string;
    workId: string;
    userId: string;
    timeZone?: string;
  },
): EmailSequencePayload => ({
  id: context.id,
  orgId: context.orgId,
  workId: context.workId,
  name: form.name.trim(),
  fromUserId: form.fromUserId,
  toEmail: form.toEmail.trim(),
  stopScope: form.stopScope,
  replyStatusId: form.replyStatusId || null,
  notifyUserIds: form.notifyUserIds,
  timeZone: context.timeZone ?? localTimeZone(),
  createdByUserId: context.userId,
  steps: form.steps.map(toStep),
});

export const buildTemplatePayload = (
  form: SequenceForm,
  context: { id?: string; orgId: string; userId: string },
): EmailSequenceTemplatePayload => ({
  id: context.id,
  orgId: context.orgId,
  name: form.name.trim(),
  stopScope: form.stopScope,
  replyStatusName: null,
  notifyUserIds: form.notifyUserIds,
  createdByUserId: context.userId,
  steps: form.steps.map((step, index) => ({
    order: index + 1,
    emailTemplateId: step.emailTemplateId || null,
    subject: step.subject.trim(),
    body: step.body,
    sendMode: index === 0 ? "immediately" : "afterDays",
    delayDays: Math.max(Number(step.delayDays) || 0, 0),
    scheduledDate: null,
  })),
});

const isBlankHtml = (html: string) =>
  !html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

/** Returns the first problem to fix, or null when the form can be saved. */
export const validateSequenceForm = (
  form: SequenceForm,
  options: {
    mode: "sequence" | "template" | "draft";
    canUseYearEnd?: boolean;
    today?: string;
  },
): string | null => {
  if (!form.name.trim()) {
    return options.mode === "template"
      ? "Enter a template name."
      : "Enter a sequence name.";
  }
  if (options.mode !== "template") {
    if (!form.fromUserId) return "Pick the mailbox to send from.";
    if (options.mode === "sequence" && !/^\S+@\S+\.\S+$/.test(form.toEmail))
      return "Pick the client's email address.";
  }
  if (!form.steps.length) return "Add at least one email.";
  if (form.steps.length > MAX_SEQUENCE_STEPS)
    return `A sequence can have at most ${MAX_SEQUENCE_STEPS} emails.`;

  for (const [index, step] of form.steps.entries()) {
    if (step.sentAt) continue;
    const label = index === 0 ? "the first email" : `email ${index + 1}`;
    if (!step.subject.trim() || isBlankHtml(step.body)) {
      return `Pick a template for ${label}.`;
    }
    if (index === 0 && options.mode !== "template") {
      if (step.sendMode === "date") {
        if (!step.scheduledDate) return "Pick the date of the first email.";
        if (options.today && step.scheduledDate < options.today)
          return "The first email can't be scheduled in the past.";
      }
      if (step.sendMode === "beforeYearEnd") {
        if (!options.canUseYearEnd)
          return "“Before year end” needs an organization with a year end.";
        if (!(step.delayDays >= 0))
          return "Enter how many days before year end.";
      }
    }
    if (index > 0 && !(Number(step.delayDays) >= 1)) {
      return `Enter after how many days ${label} is sent (1 or more).`;
    }
  }
  return null;
};

export const STOP_SCOPE_TEXT: Record<SequenceStopScope, string> = {
  all: "any connected inbox",
  mine: "your inbox",
};

const dayText = (days: number) => `${days} ${days === 1 ? "day" : "days"}`;

export const formatSequenceDate = (value?: string | null) =>
  value ? format(parseISO(value), "MMM d, yyyy") : "";

/** How the first email is sent, e.g. "immediately", "on Oct 15, 2026". */
export const describeFirstSend = (
  step: Pick<EmailSequenceStep, "sendMode" | "delayDays" | "scheduledDate">,
) => {
  if (step.sendMode === "date" && step.scheduledDate) {
    return `on ${format(parseISO(step.scheduledDate), "MMM d, yyyy")}`;
  }
  if (step.sendMode === "beforeYearEnd") {
    return `${dayText(step.delayDays || 0)} before year end`;
  }
  return "immediately";
};

export type SummaryPart = { text: string; strong?: boolean };

/** "Send Initial request to x immediately, then Reminder after 3 days. Stops…" */
export const describeSequence = (
  sequence: Pick<EmailSequence, "steps" | "toEmail" | "stopScope">,
  templateNames: Record<string, string>,
): SummaryPart[] => {
  const steps = sequence.steps.slice().sort((a, b) => a.order - b.order);
  const name = (step: EmailSequenceStep) =>
    (step.emailTemplateId && templateNames[step.emailTemplateId]) ||
    step.subject;
  const parts: SummaryPart[] = [];
  steps.forEach((step, index) => {
    if (index === 0) {
      parts.push({ text: "Send " }, { text: name(step), strong: true });
      parts.push({ text: " to " }, { text: sequence.toEmail, strong: true });
      const when = describeFirstSend(step);
      if (when.startsWith("on ")) {
        parts.push({ text: " on " }, { text: when.slice(3), strong: true });
      } else if (step.sendMode === "beforeYearEnd") {
        parts.push(
          { text: " " },
          { text: dayText(step.delayDays || 0), strong: true },
          { text: " before year end" },
        );
      } else {
        parts.push({ text: " immediately" });
      }
    } else {
      parts.push({ text: ", then " }, { text: name(step), strong: true });
      parts.push(
        { text: " after " },
        { text: dayText(step.delayDays || 0), strong: true },
      );
    }
  });
  parts.push(
    { text: ". Stops automatically when the client replies in " },
    { text: STOP_SCOPE_TEXT[sequence.stopScope], strong: true },
    { text: "." },
  );
  return parts;
};

export const getSequenceProgress = (sequence: EmailSequence) => {
  const total = sequence.steps.length;
  const sent = sequence.steps.filter((step) => !!step.sentAt).length;
  let label: string;
  if (sequence.status === "Replied") {
    label = `Client replied${
      sequence.repliedAt ? ` ${formatSequenceDate(sequence.repliedAt)}` : ""
    } · sequence stopped`;
  } else if (sequence.status === "Stopped") {
    label = `${sent} of ${total} sent · sequence stopped`;
  } else if (sequence.status === "Completed") {
    label = `All ${total} sent · no reply received`;
  } else if (sent >= total) {
    label = `All ${total} sent · waiting for a reply`;
  } else {
    label = `Step ${sent + 1} of ${total} · sending automatically`;
  }
  return { sent, total, label };
};

export const describeStartConfirm = (
  form: SequenceForm,
  yearEnd?: string | null,
) => {
  const count = form.steps.length;
  const first = form.steps[0];
  const when =
    first?.sendMode === "date" && first.scheduledDate
      ? `starting on ${format(parseISO(first.scheduledDate), "MMM d, yyyy")}`
      : first?.sendMode === "beforeYearEnd"
      ? `starting ${dayText(first.delayDays)} before year end${
          yearEnd ? ` (${yearEnd})` : ""
        }`
      : "starting now";
  return `This will send ${count} ${count === 1 ? "email" : "emails"} to ${
    form.toEmail || "the client"
  }, ${when}. The sequence stops automatically once the client replies in ${
    STOP_SCOPE_TEXT[form.stopScope]
  }.`;
};

/** Shown in the New Sequence window when the person has no mailbox of their own (QA SEQ-013). */
export const NO_OWN_MAILBOX = "Connect your mailbox first (Email page).";

export type SenderOption = {
  userId: string;
  email: string;
  name?: string | null;
};

/**
 * "Send from" choices: the person's own mailbox only (owner 2026-10-22). When editing a running
 * sequence that was started from another mailbox (e.g. a colleague's, before this rule), that
 * mailbox stays selectable as "(current)" so saving doesn't silently switch the sender.
 */
export const senderOptions = (
  own: SenderOption[],
  currentUserId?: string | null,
  currentEmail?: string | null,
): SenderOption[] => {
  if (!currentUserId || own.some((box) => box.userId === currentUserId)) {
    return own;
  }
  return [
    {
      userId: currentUserId,
      email: `${currentEmail || "Current mailbox"} (current)`,
    },
    ...own,
  ];
};
